import { useEffect, useState, useCallback } from "react";
import api, { brl } from "../lib/api";
import {
  Search, ShieldCheck, AlertTriangle, Loader2,
  Clock, TrendingUp, ChevronRight,
} from "lucide-react";

/**
 * Componente compacto de histórico por IMEI (para embed no modal Nova OS).
 * Props:
 *   imei: string — quando >= 6 dígitos, faz fetch
 *   currentOsId: opcional — se estivermos editando uma OS existente, exclui ela do histórico
 *   onFill: opcional — callback ({brand, device, customer_name, customer_phone}) para autopreencher a OS
 */
export default function ImeiHistoryInline({ imei, currentOsId, onFill }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const digits = String(imei || "").replace(/\D/g, "");
  const valid = digits.length >= 6;

  useEffect(() => {
    if (!valid) {
      setData(null);
      setErr(null);
      return;
    }
    let cancel = false;
    setLoading(true);
    setErr(null);
    api
      .get(`/imei/${digits}/history`)
      .then((r) => {
        if (!cancel) setData(r.data);
      })
      .catch((e) => {
        if (!cancel) setErr(e?.response?.data?.detail || "Erro ao buscar");
      })
      .finally(() => {
        if (!cancel) setLoading(false);
      });
    return () => {
      cancel = true;
    };
  }, [digits, valid]);

  if (!valid) return null;
  if (loading) {
    return (
      <div className="text-xs text-slate-500 flex items-center gap-1 mt-1">
        <Loader2 size={12} className="animate-spin" /> Consultando histórico...
      </div>
    );
  }
  if (err) return <div className="text-xs text-red-500 mt-1">⚠️ {err}</div>;
  if (!data) return null;

  const history = (data.history || []).filter((h) => h.id !== currentOsId);
  const total = history.length;

  if (total === 0) {
    return (
      <div
        className="mt-2 text-xs bg-blue-50 border border-blue-200 rounded-sm p-2 text-blue-800"
        data-testid="imei-history-empty"
      >
        ✨ Este é o <b>primeiro atendimento</b> desse aparelho na loja.
      </div>
    );
  }

  const w = data.warranty;
  const fill = () => {
    if (!onFill || !data.last_device) return;
    onFill({
      brand: data.last_device.brand || "",
      device: data.last_device.device || "",
      customer_name: data.last_device.customer_name || "",
      customer_phone: data.last_device.customer_phone || "",
    });
  };

  return (
    <div
      className="mt-2 border border-slate-200 rounded-sm p-3 space-y-2"
      data-testid="imei-history-panel"
    >
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-700 font-semibold">
          <Search size={12} className="text-[#2563EB]" />
          Histórico deste aparelho
        </div>
        <button
          type="button"
          onClick={fill}
          className="text-[10px] font-semibold text-[#2563EB] hover:underline"
          data-testid="imei-history-fill"
          title="Autopreencher marca/modelo/cliente com dados do último atendimento"
        >
          Usar dados anteriores →
        </button>
      </div>

      {/* Garantia ativa */}
      {w?.active && (
        <div
          className="bg-emerald-50 border border-emerald-300 rounded-sm p-2 flex items-start gap-2"
          data-testid="imei-warranty-active"
        >
          <ShieldCheck size={16} className="text-emerald-600 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-emerald-900 leading-tight">
            <b>🛡️ GARANTIA ATIVA</b> por mais <b>{w.days_remaining} dia(s)</b>.
            <br />
            Serviço: <i>{w.service}</i> (OS #{w.from_os_short})
            <br />
            <span className="text-emerald-700">
              Se o problema atual for do mesmo serviço, o reparo é gratuito.
            </span>
          </div>
        </div>
      )}

      {/* Problemas recorrentes */}
      {data.recurring_problems?.length > 0 && (
        <div
          className="bg-amber-50 border border-amber-300 rounded-sm p-2 flex items-start gap-2"
          data-testid="imei-recurring"
        >
          <AlertTriangle size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-amber-900">
            <b>Problemas recorrentes:</b>{" "}
            {data.recurring_problems.map((p) => `"${p.problem}" (${p.count}x)`).join(", ")}
          </div>
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-3 gap-1 text-center">
        <div className="bg-slate-50 py-1.5 rounded-sm">
          <div className="text-[9px] uppercase tracking-widest text-slate-500">Visitas</div>
          <div className="font-bold text-slate-900" data-testid="imei-total-visits">
            {total}
          </div>
        </div>
        <div className="bg-slate-50 py-1.5 rounded-sm">
          <div className="text-[9px] uppercase tracking-widest text-slate-500">Gasto total</div>
          <div className="font-bold text-slate-900" data-testid="imei-total-spent">
            {brl(data.total_spent)}
          </div>
        </div>
        <div className="bg-slate-50 py-1.5 rounded-sm">
          <div className="text-[9px] uppercase tracking-widest text-slate-500">Cliente</div>
          <div
            className="font-semibold text-slate-900 text-[11px] truncate px-1"
            title={data.last_device?.customer_name || "—"}
          >
            {data.last_device?.customer_name || "—"}
          </div>
        </div>
      </div>

      {/* Lista */}
      <div className="max-h-40 overflow-y-auto border-t border-slate-100 pt-1 space-y-1">
        {history.slice(0, 5).map((h) => {
          const d = (h.created_at || "").slice(0, 10);
          const [y, m, dd] = d.split("-");
          const display = y ? `${dd}/${m}/${y}` : "—";
          return (
            <div
              key={h.id}
              className="flex items-center gap-2 text-xs py-1 border-b border-slate-50 last:border-0"
              data-testid={`imei-row-${h.id}`}
            >
              <Clock size={11} className="text-slate-400" />
              <span className="text-slate-500 w-16">{display}</span>
              <span className="flex-1 truncate">
                <b>{h.problem}</b>
                {h.diagnosis && (
                  <span className="text-slate-500"> · {h.diagnosis}</span>
                )}
              </span>
              <span className="font-semibold text-slate-800">{brl(h.price)}</span>
              <span
                className={`text-[9px] uppercase px-1.5 py-0.5 rounded-sm font-bold ${
                  h.status === "entregue"
                    ? "bg-blue-100 text-blue-700"
                    : h.status === "pronto"
                    ? "bg-emerald-100 text-emerald-700"
                    : "bg-slate-100 text-slate-600"
                }`}
              >
                {h.status_label}
              </span>
            </div>
          );
        })}
        {history.length > 5 && (
          <a
            href={`/app/imei-lookup?imei=${digits}`}
            target="_blank"
            rel="noopener noreferrer"
            className="block text-center text-xs text-[#2563EB] hover:underline pt-1 flex items-center justify-center gap-1"
          >
            Ver todas as {history.length} visitas <ChevronRight size={11} />
          </a>
        )}
      </div>
    </div>
  );
}
